import { type Client, createClient } from "@khazzin/api-client";
import { useFlag as useFlagFor } from "@khazzin/api-client/query";
import { useAccessToken, useInterface } from "@khazzin/auth";
import { createContext, type ReactNode, useContext, useMemo, useState } from "react";

const ApiContext = createContext<Client | null>(null);
const UpgradeContext = createContext(false);

/** This build, sent as `X-App-Version` and shown on Profile. */
export const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? "dev";

/** The API client. Throws outside {@link ApiClientProvider}, which is a wiring bug. */
export function useApi(): Client {
	const api = useContext(ApiContext);
	if (api === null) {
		throw new Error("useApi must be used inside <ApiClientProvider>");
	}
	return api;
}

/**
 * Whether the server has refused this build (EP10-S23): any answer was 426.
 * It stays set until the app reloads onto a newer one.
 */
export function useUpgradeRequired(): boolean {
	return useContext(UpgradeContext);
}

/** Whether a feature flag is on for this app's interface (EP10-S23). Absent is off. */
export function useFlag(key: string): boolean {
	return useFlagFor(useApi(), useInterface(), key);
}

/**
 * A client built from the live token that names this app on every request —
 * `/me/context` included — through the interface `AuthProvider` was given,
 * and this build's version.
 */
export function ApiClientProvider({ children }: { children: ReactNode }) {
	const accessToken = useAccessToken();
	const caller = useInterface();
	const [upgrade, setUpgrade] = useState(false);
	const api = useMemo(
		() =>
			createClient({
				accessToken,
				interface: caller,
				// Read off the document, as the Admin Console does, so a language
				// switch changes the next request's Accept-Language.
				locale: () => document.documentElement.lang || null,
				appVersion: APP_VERSION,
				onUpgradeRequired: () => setUpgrade(true),
			}),
		[accessToken, caller],
	);
	return (
		<ApiContext.Provider value={api}>
			<UpgradeContext.Provider value={upgrade}>{children}</UpgradeContext.Provider>
		</ApiContext.Provider>
	);
}
